import sortBy from 'lodash/sortBy'
import {GenericSelector} from '@freckle/educator-entities/ts/common/components/generic-selector'
import {createSelectorOptionAsItem} from '@freckle/educator-entities/ts/common/logic/generic-selector'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {nameForSorting, formattedName} from '@freckle/educator-entities/ts/users/models/student'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {GenericPicker} from '@freckle/educator-materials/ts/components/generic-picker'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {Modal, Header, Body, Footer} from '@freckle/educator-materials/ts/components/modal'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {gradePicker, studentPicker as studenPickerClass} from './add-assessment-modal.module.scss'

type Props = {
  show: boolean
  onHide: () => void
  students: Array<StudentAttrs>
  accessibleGrades: number[]
  selectedGrade: number
  selectedStudentIds: Array<number>
  onChangeGrade: (grade: number) => void
  onToggleStudent: (studentId: number) => void
  onClickSelectAll: () => void
  onClickDeselectAll: () => void
  onSubmit: () => void
}

export default function AddAssessmentModal(props: Props) {
  const {t} = useSafeTranslation()
  const {
    students,
    accessibleGrades,
    show,
    onHide,
    selectedGrade,
    selectedStudentIds,
    onChangeGrade,
    onToggleStudent,
    onClickSelectAll,
    onClickDeselectAll,
    onSubmit
  } = props

  const gradeOptions = accessibleGrades.map(grade =>
    createSelectorOptionAsItem(grade, t('DISPLAY_GRADE', {grade}))
  )

  const studentOptions = sortBy(
    students.filter(student => student.grade === selectedGrade),
    student => nameForSorting(student)
  ).map(student => [student.id, formattedName(student), null]) as [number, string, null][]

  const studentPicker = (
    <GenericPicker
      options={studentOptions}
      selection={selectedStudentIds}
      onClickOption={onToggleStudent}
      onClickBatchSelect={selectAll => (selectAll ? onClickSelectAll() : onClickDeselectAll())}
      counterTitle={t('ASSESSMENTS_MODAL_STUDENTS_SELECTED_TEXT')}
      layout="rows"
    />
  )

  return (
    <Modal show={show} onHide={onHide} height="large">
      <Header onClickClose={onHide}>{t('ASSESSMENTS_MODAL_TITLE')}</Header>
      <Body>
        <p>{t('ASSESSMENTS_MODAL_DESCRIPTION_TEXT')}</p>
        <div className={gradePicker}>
          <Label>{t('ASSESSMENTS_MODAL_GRADE_SELECTOR_LABEL')}</Label>
          <GenericSelector
            dataTest="add-assessment-modal-generic-selector"
            onChangeSelection={onChangeGrade}
            selection={selectedGrade}
            collection={gradeOptions}
          />
        </div>

        <div data-pii className={studenPickerClass}>
          <Label>{t('ASSESSMENTS_MODAL_STUDENTS_PICKER_LABEL_TEXT')}</Label>
          {studentPicker}
        </div>
      </Body>
      <Footer>
        <Button style="secondary" onClick={onHide}>
          {t('ASSESSMENTS_MODAL_CANCEL_BUTTON_TEXT')}
        </Button>
        <Button dataTest="add-assessment-modal-primary-button" style="primary" onClick={onSubmit}>
          {t('ASSESSMENTS_MODAL_CREATE_ASSESSMENT_BUTTON_TEXT')}
        </Button>
      </Footer>
    </Modal>
  )
}
